/**
 * Formatting helpers — locale follows the active UI language (en/es).
 */
import { format } from 'date-fns';
import i18n, { activeDateLocale } from '@/i18n';
import { uiLocale } from './dates';

export function fmtInt(n: number): string {
  return new Intl.NumberFormat(uiLocale(), { maximumFractionDigits: 0 }).format(n);
}

export function fmtDecimal(n: number, decimals = 1): string {
  return new Intl.NumberFormat(uiLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

export function fmtPercent(n: number, decimals = 1): string {
  return `${fmtDecimal(n, decimals)}%`;
}

export function fmtHours(minutes: number): string {
  return new Intl.NumberFormat(uiLocale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(minutes / 60);
}

/** 95 → "95 min" · 150 → "2 h 30 min" · 120 → "2 h" */
export function fmtDuration(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "Tuesday, October 14, 2025" / "martes, 14 de octubre de 2025" */
export function fmtDateLong(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const pattern = i18n.language === 'es' ? "EEEE, d 'de' MMMM 'de' yyyy" : 'EEEE, MMMM d, yyyy';
  return format(d, pattern, { locale: activeDateLocale() });
}

/** "14 Oct 2025" / "14 oct 2025" */
export function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return format(d, 'd MMM yyyy', { locale: activeDateLocale() });
}

/** Leading professional title (Dr. / Dra. / Prof. …) — used when composing display names. */
const TITLE_PREFIX_RE = /^(dr\.?|dra\.?|prof\.?|profa\.?)\s+/i;

/**
 * Strip a leading title so the form can show a fixed "Dr." prefix.
 * Preserves trailing whitespace — callers wire this into controlled inputs
 * on every keystroke, and trimming would eat the space between given/family name.
 */
export function stripTitlePrefix(name: string): string {
  return name.replace(/^\s+/, '').replace(TITLE_PREFIX_RE, '');
}

/** Ensure a non-empty name carries a "Dr." prefix (keeps Dra./Prof. if already present). */
export function withDrPrefix(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  if (TITLE_PREFIX_RE.test(trimmed)) return trimmed;
  return `Dr. ${trimmed}`;
}

/** Avatar initials: "Dr. Elena Navarro" → "EN" */
export function initialsOf(name: string): string {
  const clean = name.trim();
  if (!clean) return 'Dr';
  const parts = clean.split(/\s+/).filter((p) => p.length > 0 && !p.endsWith('.'));
  const letters = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
  return letters.join('') || 'Dr';
}

/** Greeting key by hour of day (translated in dashboard.greeting.*). */
export function greetingKeyByTime(hour = new Date().getHours()): 'morning' | 'afternoon' | 'evening' {
  if (hour >= 7 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 19) return 'afternoon';
  return 'evening';
}

/** Capitalize the first character of a string; leave the rest untouched. */
export function capitalizeFirst(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** Capitalize the first letter of each sentence; leave the rest untouched. */
export function sentenceCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.replace(/(^\s*\w|[.!?]\s+\w)/g, (m) =>
    m.replace(/\w/, (c) => c.toUpperCase()),
  );
}

/**
 * Soften a SNOMED preferred term for UI lists.
 * Spanish RF2 stores procedure terms lowercase (`vasectomía`); English is
 * already sentence-cased. Always capitalize the first letter so both locales
 * read as a proper label.
 */
export function displayCatalogTerm(term: string): string {
  return capitalizeFirst(term);
}

/**
 * Soften a SNOMED body-structure label for organ headers.
 * Most Spanish sites are "estructura de/del/… X"; English ends in " structure".
 * Strip that wrapper, then capitalize — `estructura del riñón` → `Riñón`.
 */
export function displayBodySiteTerm(term: string): string {
  let cleaned = term.trim();
  cleaned = cleaned.replace(
    /^estructura\s+(?:de\s+los\s+|de\s+las\s+|de\s+la\s+|del\s+|de\s+)?/i,
    '',
  );
  cleaned = cleaned.replace(/\s+structure(?:\s*\([^)]*\))?$/i, '');
  return capitalizeFirst(cleaned);
}
